import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AuthLayout from "./components/AuthLayout";
import { clearAuthFlow, getAuthFlow } from "./components/authStore";
import { AuthService } from "../../services/auth.service";
import AuthPopup from "./components/AuthPopup";

export default function ResetPassword() {
  const nav = useNavigate();
  const flow = getAuthFlow();
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [s1, setS1] = useState(false);
  const [s2, setS2] = useState(false);
  const [popup, setPopup] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
    onClose: null,
  });

  const showPopup = ({ type = "error", title = "Alert", message, onClose = null }) => {
    setPopup({ open: true, type, title, message, onClose });
  };

  const closePopup = () => {
    const cb = popup.onClose;
    setPopup((p) => ({ ...p, open: false, onClose: null }));
    cb?.();
  };

  const inputBase =
    "w-full h-[46px] rounded-[10px] border border-[#f2c6cc] bg-white px-4 text-[14px] text-slate-700 placeholder-slate-400 shadow-[0_12px_26px_rgba(216,106,120,0.14)] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]";

  const buttonBase =
    "w-full h-[46px] rounded-[10px] bg-[#d86a78] text-white font-semibold shadow-[0_12px_26px_rgba(216,106,120,0.22)] hover:bg-[#c95b69] active:scale-[0.99] transition";

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!p1 || !p2) {
      return showPopup({
        type: "error",
        title: "Enter Password",
        message: "Please enter both password fields",
      });
    }

    if (p1 !== p2) {
      return showPopup({
        type: "error",
        title: "Password Mismatch",
        message: "Passwords do not match",
      });
    }

    if (!flow?.resetEmail || !flow?.otp) {
      return showPopup({
        type: "error",
        title: "Session Expired",
        message: "Session expired. Please restart forgot password.",
        onClose: () => {
          clearAuthFlow();
          nav("/admin/forgot-password", { replace: true });
        },
      });
    }

    try {
      await AuthService.forgotVerifyAndSetPassword({
        email: flow.resetEmail,
        otp: flow.otp,
        newPassword: p1,
        confirmPassword: p2,
      });

      showPopup({
        type: "success",
        title: "Password Updated",
        message: "Password updated successfully. Please login again.",
        onClose: () => {
          nav("/admin/login", { replace: true });

          setTimeout(() => {
            clearAuthFlow();
          }, 300);
        },
      });
    } catch (err) {
      showPopup({
        type: "error",
        title: "Reset Failed",
        message: err?.message || "Unable to reset password",
      });
    }
  };

  return (
    <AuthLayout>
      <h1 className="mb-7 text-center text-[32px] font-bold tracking-wide text-[#d86a78]">
        ADMIN LOGIN
      </h1>

      <div className="mx-auto w-full max-w-[360px]">
        <form onSubmit={onSubmit} className="space-y-6">
          <div className="space-y-2">
            <label className="text-[12px] font-semibold tracking-wider text-slate-500">
              New Password
            </label>
            <div className="relative">
              <input
                className={inputBase + " pr-12"}
                type={s1 ? "text" : "password"}
                value={p1}
                onChange={(e) => setP1(e.target.value)}
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setS1((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-2 text-slate-500 hover:bg-slate-100"
              >
                {s1 ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-[12px] font-semibold tracking-wider text-slate-500">
              Confirm Password
            </label>
            <div className="relative">
              <input
                className={inputBase + " pr-12"}
                type={s2 ? "text" : "password"}
                value={p2}
                onChange={(e) => setP2(e.target.value)}
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setS2((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-2 text-slate-500 hover:bg-slate-100"
              >
                {s2 ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button type="submit" className={buttonBase}>
            SUBMIT
          </button>
        </form>
      </div>
      <AuthPopup
        open={popup.open}
        type={popup.type}
        title={popup.title}
        message={popup.message}
        onClose={closePopup}
      />
    </AuthLayout>
  );
}